import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Typography,
    useMediaQuery,
    IconButton,
    Dialog,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";

const borderStyles = {
    border: "0.92px solid",
    borderImageSource:
        "radial-gradient(80.38% 222.5% at -13.75% -12.36%, #98F9FF 0%, rgba(255, 255, 255, 0) 100%), radial-gradient(80.69% 208.78% at 108.28% 112.58%, #EABFFF 0%, rgba(135, 38, 183, 0) 100%)",
};

const MediaViewModel = ({
    open,
    onClose,
    videoUrl = "",
}) => {
    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const videoRef = useRef(null);
    const progressBarRef = useRef(null);
    const controlsTimeoutRef = useRef(null);

    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(true);
    const [progress, setProgress] = useState(0);
    const [showControls, setShowControls] = useState(true);

    const currentVideo = videoUrl || "";

    useEffect(() => {
        setProgress(0);
        setIsPlaying(true);
        setShowControls(true);
    }, [videoUrl]);

    const isYouTubeUrl = (url = "") => {
        return /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)/i.test(
            url
        );
    };

    const getYouTubeEmbedUrl = (url = "") => {
        let videoId = "";

        if (url.includes("youtu.be/")) {
            videoId = url.split("youtu.be/")[1]?.split("?")[0];
        } else if (url.includes("/embed/")) {
            videoId = url.split("/embed/")[1]?.split("?")[0];
        } else {
            const match = url.match(/[?&]v=([^&]+)/);
            videoId = match?.[1];
        }

        return videoId
            ? `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`
            : "";
    };

    const isVideoFile = (url = "") => {
        return /\.(mp4|webm|ogg|mov|m4v)$/i.test(url);
    };

    const togglePlay = () => {
        if (!videoRef.current) return;

        if (isPlaying) {
            videoRef.current.pause();
        } else {
            videoRef.current.play();
        }

        setIsPlaying(!isPlaying);
    };

    const toggleMute = () => {
        if (!videoRef.current) return;

        videoRef.current.muted = !videoRef.current.muted;
        setIsMuted(videoRef.current.muted);
    };

    const toggleFullscreen = () => {
        if (!videoRef.current) return;

        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            videoRef.current.requestFullscreen();
        }
    };

    const handleTimeUpdate = () => {
        if (!videoRef.current) return;

        const current =
            (videoRef.current.currentTime / videoRef.current.duration) * 100;

        setProgress(current || 0);
    };

    const handleProgressBarClick = (e) => {
        if (!progressBarRef.current || !videoRef.current) return;

        const rect = progressBarRef.current.getBoundingClientRect();

        const position = (e.clientX - rect.left) / rect.width;

        videoRef.current.currentTime =
            position * videoRef.current.duration;
    };

    const handleMouseMove = () => {
        setShowControls(true);

        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
        }

        controlsTimeoutRef.current = setTimeout(() => {
            if (isPlaying) {
                setShowControls(false);
            }
        }, 3000);
    };

    const formatTime = (seconds) => {
        if (!seconds || Number.isNaN(seconds)) return "0:00";

        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);

        return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
    };

    const currentTime = videoRef.current
        ? formatTime(videoRef.current.currentTime)
        : "0:00";

    const duration = videoRef.current
        ? formatTime(videoRef.current.duration)
        : "0:00";

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    overflow: "hidden",
                    maxWidth: "1200px",
                    background:
                        "radial-gradient(90.16% 143.01% at 15.32% 21.04%, rgba(165, 238, 255, 0.27) 0%, rgba(110, 191, 244, 0.05) 77.08%, rgba(86, 155, 220, 0.24) 100%)",
                    ...borderStyles,
                    borderRadius: 2,
                    backdropFilter: "blur(10px)",
                },
            }}
        >
            <IconButton
                onClick={onClose}
                sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    zIndex: 9999,
                    color: "#fff",
                    background: "rgba(0,0,0,0.6)",
                    "&:hover": {
                        background: "rgba(0,0,0,0.8)",
                    },
                }}
            >
                <CloseIcon />
            </IconButton>

            <Box
                sx={{
                    width: "100%",
                    p: { xs: 1, md: 3 },
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        width: "100%",
                        height: {
                            xs: "250px",
                            sm: "400px",
                            md: "580px",
                        },
                        backgroundColor: "#000",
                        overflow: "hidden",
                        borderRadius: "12px",
                    }}
                    onMouseMove={handleMouseMove}
                >
                    {isYouTubeUrl(currentVideo) ? (
                        <iframe
                            src={getYouTubeEmbedUrl(currentVideo)}
                            title="Youtube Video"
                            width="100%"
                            height="100%"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{
                                border: "none",
                            }}
                        />
                    ) : isVideoFile(currentVideo) ? (
                        <>
                            <video
                                ref={videoRef}
                                src={currentVideo}
                                autoPlay
                                muted={isMuted}
                                playsInline
                                preload="metadata"
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                }}
                                onTimeUpdate={handleTimeUpdate}
                                onEnded={() => setIsPlaying(false)}
                            />

                            <Box
                                ref={progressBarRef}
                                onClick={handleProgressBarClick}
                                sx={{
                                    position: "absolute",
                                    bottom: 50,
                                    left: 0,
                                    right: 0,
                                    height: 5,
                                    cursor: "pointer",
                                    background: "rgba(255,255,255,0.2)",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: `${progress}%`,
                                        height: "100%",
                                        background: "#98F9FF",
                                    }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    position: "absolute",
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    p: 1,
                                    background:
                                        "linear-gradient(transparent, rgba(0,0,0,0.7))",
                                    color: "#fff",
                                    opacity: showControls ? 1 : 0,
                                    transition: "0.3s",
                                }}
                            >
                                <IconButton color="inherit" onClick={togglePlay}>
                                    {isPlaying ? (
                                        <PauseIcon />
                                    ) : (
                                        <PlayArrowIcon />
                                    )}
                                </IconButton>

                                <IconButton color="inherit" onClick={toggleMute}>
                                    {isMuted ? (
                                        <VolumeOffIcon />
                                    ) : (
                                        <VolumeUpIcon />
                                    )}
                                </IconButton>

                                <Typography variant="body2">
                                    {currentTime} / {duration}
                                </Typography>

                                <Box sx={{ flex: 1 }} />

                                <IconButton
                                    color="inherit"
                                    onClick={toggleFullscreen}
                                >
                                    <FullscreenIcon />
                                </IconButton>
                            </Box>
                        </>
                    ) : (
                        <img
                            src={currentVideo}
                            alt="Media"
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                            }}
                        />
                    )}
                </Box>
            </Box>
        </Dialog>
    );
};

export default MediaViewModel;